import React, { useState, useEffect, useRef } from "react";
import { apiGet } from "../../services/api";

export interface PatientSearchItem {
  patient_id: string;
  phone?: string;
  name?: string;
  age?: number | string;
  gender?: string;
  color?: string;
}

export interface QuickPatientSearchProps {
  onPatientSelect?: (p: PatientSearchItem) => void;
  onEdit?: (p: PatientSearchItem) => void;
  placeholder?: string;
}

const QuickPatientSearch = ({ onPatientSelect, onEdit, placeholder }: QuickPatientSearchProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PatientSearchItem[]>([]);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = async (value: string) => {
    setQuery(value);
    const q = value.trim();
    if (q.length < 2) {
      setResults([]);
      setVisible(false);
      setError(null);
      return;
    }
    setLoading(true);
    setVisible(true);
    try {
      const data = await apiGet(`/api/patients/search?q=${encodeURIComponent(q)}`);
      setResults(Array.isArray(data) ? (data as PatientSearchItem[]) : []);
      setError(null);
    } catch (err) {
      console.error("Patient search failed:", err);
      setResults([]);
      setError("Unable to search right now. Check server connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (patient: PatientSearchItem) => {
    const normalized: PatientSearchItem = {
      patient_id: patient.patient_id,
      phone: patient.patient_id,
      name: patient.name,
      age: patient.age,
      gender: patient.gender,
      color: patient.color,
    };
    onPatientSelect?.(normalized);
    setQuery("");
    setResults([]);
    setVisible(false);
  };

  return (
    <div className="rc-search" ref={wrapperRef}>
      <div className="rc-search-input-wrap">
        <span className="rc-search-icon">🔍</span>
        <input
          type="text"
          className="rc-search-input"
          placeholder={placeholder ?? "Search patient (Name, Mobile, ID)..."}
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
          onFocus={() => {
            if (query.length >= 2 && results.length > 0) setVisible(true);
          }}
        />
        {loading && <div className="rc-search-spinner" />}
      </div>
      {visible && (
        <ul className="rc-search-results">
          {error && !loading ? (
            <li className="rc-search-error">
              <span>⚠️ {error}</span>
            </li>
          ) : results.length > 0 ? (
            results.map((p) => (
              <li
                key={p.patient_id}
                className="rc-search-item"
                onClick={() => handleSelect(p)}
              >
                <div className="rc-search-avatar">
                  {p.name?.charAt(0)?.toUpperCase() ?? "?"}
                </div>
                <div className="rc-search-meta">
                  <strong className="rc-search-name">{p.name}</strong>
                  <div className="rc-search-sub">
                    <span>📱 {p.patient_id}</span>
                    <span>•</span>
                    <span>{p.age ?? "?"}y</span>
                    <span>•</span>
                    <span>{p.gender ?? "?"}</span>
                  </div>
                </div>
                {onEdit && (
                  <button
                    type="button"
                    className="rc-search-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(p);
                    }}
                  >
                    ✏️
                  </button>
                )}
              </li>
            ))
          ) : (
            !loading && (
              <li className="rc-search-empty">
                <span>🚫 No patients found</span>
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
};

export default QuickPatientSearch;
